// src/components/Auth/LoginModal.jsx
import React, { useState } from 'react';
import { signIn } from '../../services/auth';
import { Link } from 'react-router-dom';

const LoginModal = ({ isOpen, onClose, onSwitchToSignup }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        const result = await signIn(email, password);
        if (result.success) {
            onClose();
            window.location.reload();
        } else {
            const errorMsg = result.error;
            if (errorMsg.includes('invalid-credential') || errorMsg.includes('wrong-password')) {
                setError('Invalid email or password. Please try again.');
            } else if (errorMsg.includes('user-not-found')) {
                setError('No account found with this email. Please sign up first.');
            } else if (errorMsg.includes('too-many-requests')) {
                setError('Too many failed attempts. Please try again later or reset your password.');
            } else if (errorMsg.includes('network-request-failed')) {
                setError('Network error. Please check your internet connection.');
            } else {
                setError('Login failed. Please try again.');
            }
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative animate-fadeIn" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 text-2xl z-10">
                    <i className="fas fa-times"></i>
                </button>

                <div className="p-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="fas fa-sign-in-alt text-white text-2xl"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-gradpath-dark">Welcome Back</h2>
                        <p className="text-gray-500 text-sm">Login to continue your journey</p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <div className="relative">
                                <i className="fas fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-gradpath-green"></i>
                                <input
                                    type="email"
                                    placeholder="Email Address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                                />
                            </div>
                        </div>
                        
                        <div className="mb-4">
                            <div className="relative">
                                <i className="fas fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-gradpath-green"></i>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gradpath-green"
                                >
                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <div className="flex justify-between items-center mb-6">
                            <label className="flex items-center gap-2 text-sm text-gray-600">
                                <input type="checkbox" className="rounded text-gradpath-green" />
                                Remember me
                            </label>
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    document.getElementById('forgotPasswordBtn')?.click();
                                }}
                                className="text-sm text-gradpath-green hover:underline"
                            >
                                Forgot Password?
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white font-semibold py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                        >
                            {loading ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-sign-in-alt mr-2"></i> Login</>}
                        </button>
                    </form>

                    <div className="text-center mt-6 pt-4 border-t border-gray-200 space-y-3">
                        <p className="text-sm text-gray-600">
                            Don't have an account?{' '}
                            <button onClick={onSwitchToSignup} className="text-gradpath-green font-semibold hover:underline">
                                Sign up here
                            </button>
                        </p>
                        <div className="pt-2">
                            <Link
                                to="/for-recruiters"
                                onClick={onClose}
                                className="text-sm text-gradpath-green hover:underline font-medium inline-flex items-center gap-1"
                            >
                                <i className="fas fa-building text-xs"></i>
                                Sign in as Recruiter
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginModal;